/**
 * TanStack Query hooks for Recent Chats (AI Chat V1.1 Item 4 — DR #43).
 *
 * GET endpoints sit on a shallow staleTime so the sidebar refreshes when the
 * user comes back from another tab. Mutations do optimistic updates against
 * both the React Query cache and the chatStore so the sidebar feels instant.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type ChatMessageRow, type ChatSessionSummary } from '../client';
import { useChatStore, type SessionMeta } from '../../stores/chatStore';

export const chatSessionKeys = {
  all: ['chat-sessions'] as const,
  lists: () => [...chatSessionKeys.all, 'list'] as const,
  messages: (sessionId: string) => [...chatSessionKeys.all, 'messages', sessionId] as const,
};

function summaryToMeta(s: ChatSessionSummary): SessionMeta {
  return {
    id: s.id,
    title: s.title,
    dealId: s.deal_id,
    dealName: s.deal_name,
    language: s.language,
    startedAt: s.started_at,
    lastMessageAt: s.last_message_at,
    messageCount: s.message_count,
  };
}

export function useChatSessions(limit = 20, enabled = true) {
  const setSessions = useChatStore((s) => s.setSessions);

  return useQuery({
    queryKey: chatSessionKeys.lists(),
    queryFn: async () => {
      const rows = await api.chat.listSessions(limit);
      const metas = rows.map(summaryToMeta);
      setSessions(metas);
      return metas;
    },
    staleTime: 30 * 1000,
    enabled,
  });
}

export function useChatSessionMessages(sessionId: string | null) {
  return useQuery({
    queryKey: sessionId ? chatSessionKeys.messages(sessionId) : ['chat-messages', 'disabled'],
    queryFn: () => api.chat.getSessionMessages(sessionId as string),
    enabled: !!sessionId,
  });
}

export function useRenameSession() {
  const queryClient = useQueryClient();
  const updateSessionTitle = useChatStore((s) => s.updateSessionTitle);

  return useMutation({
    mutationFn: ({ sessionId, title }: { sessionId: string; title: string }) =>
      api.chat.renameSession(sessionId, title),
    onMutate: ({ sessionId, title }) => {
      updateSessionTitle(sessionId, title);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatSessionKeys.lists() });
    },
  });
}

export function useDeleteSession() {
  const queryClient = useQueryClient();
  const removeSession = useChatStore((s) => s.removeSession);

  return useMutation({
    mutationFn: (sessionId: string) => api.chat.deleteSession(sessionId),
    onMutate: (sessionId) => {
      removeSession(sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chatSessionKeys.lists() });
    },
  });
}

export function useRestoreSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => api.chat.restoreSession(sessionId),
    onSuccess: () => {
      // Re-fetch the full list so the restored session reappears at its
      // correct ordering (last_message_at).
      queryClient.invalidateQueries({ queryKey: chatSessionKeys.lists() });
    },
  });
}

export type { ChatMessageRow, ChatSessionSummary };
